/**
 * Políticas de garantía — una por marca, más la "por defecto" para las
 * marcas sin política propia.
 *
 * La garantía la respalda el dealer (decisión del negocio), así que no hay
 * campo de quién respalda. El plan de mantenimientos va dentro de la
 * política; al vender se copia a la garantía, por lo que editar aquí solo
 * afecta las ventas nuevas.
 *
 * Abajo, para quien tenga configuracion.modificar, la carga inicial: crea las
 * garantías de las ventas anteriores a la activación desde una fecha.
 */
import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  FP,
  StatusPill,
  ConfirmarDialog,
  fpInputSx,
  fpMultilineInputSx,
} from "components/financepro";
import { useApi } from "lib/api";
import { validarPolitica, aIso, sumarMeses, mesesDePiezas } from "./calendario";
import { btnSolid, btnOutline, btnBase, sectionBox, etiquetaSeccion } from "./comunes";

const nombrePolitica = (p) => (p.porDefecto ? "Por defecto" : p.marca);

const nuevaPolitica = () => ({
  id: null,
  marca: "",
  porDefecto: false,
  mesesNueva: 12,
  mesesUsada: 3,
  cubre: "",
  noCubre: "",
  descripcion: "",
  piezas: [],
  servicios: [
    { numero: 1, nombre: "Primer servicio", dias: 30, kmRef: 500, tolerancia: 10, precio: 0 },
  ],
});

const soloEntero = (v) => String(v).replace(/[^\d]/g, "");

function CargaInicial({ toast }) {
  const { post } = useApi();
  const [desde, setDesde] = useState(sumarMeses(aIso(new Date()), -12));
  const [cargando, setCargando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/garantias_cargainicial", { desde });
      if (!ok) {
        toast.error(data?.Error || "No se pudo hacer la carga inicial");
        return;
      }
      toast.exito(
        `Carga terminada: ${data.creadas} garantías creadas de ${data.revisadas} ventas revisadas`
      );
      setConfirmar(false);
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{ ...sectionBox, mt: 2.5 }}>
      <Typography sx={etiquetaSeccion}>Carga inicial</Typography>
      <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mt: 0.5, mb: 2 }}>
        Crea las garantías de las ventas hechas antes de activar el módulo, con la política actual
        de cada marca. Se puede repetir: no duplica.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        <TextField
          type="date"
          label="Ventas desde"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: aIso(new Date()) }}
          sx={{ ...fpInputSx, minWidth: 200 }}
        />
        <Button onClick={() => setConfirmar(true)} disabled={!desde} sx={btnOutline()}>
          Cargar garantías
        </Button>
      </Stack>
      <ConfirmarDialog
        abierto={confirmar}
        titulo="Carga inicial de garantías"
        mensaje="Se crearán las garantías de las ventas al cliente final desde esa fecha que todavía no tengan. Las que ya existen no se tocan."
        textoConfirmar="Cargar"
        cargando={cargando}
        onCerrar={() => setConfirmar(false)}
        onConfirmar={cargar}
      />
    </Box>
  );
}

export default function PoliticasGarantia({ config, toast }) {
  const { post } = useApi();
  const permisos = config.permisos || {};
  const [politicas, setPoliticas] = useState([]);
  const [marcasCatalogo, setMarcasCatalogo] = useState([]);
  const [seleccion, setSeleccion] = useState(null);
  const [borrador, setBorrador] = useState(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = async (seleccionar) => {
    const { ok, data } = await post("/api/garantias_politicas", {});
    if (!ok) {
      toast.error(data?.Error || "No se pudieron leer las políticas");
      return;
    }
    const lista = data.politicas || [];
    setPoliticas(lista);
    setMarcasCatalogo(data.marcasCatalogo || []);
    setSeleccion(seleccionar ?? lista[0]?.id ?? null);
  };

  useEffect(() => {
    cargar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (seleccion === "nueva") return;
    const p = politicas.find((x) => x.id === seleccion);
    setBorrador(p ? JSON.parse(JSON.stringify(p)) : null);
  }, [seleccion, politicas]);

  const set = (campo, valor) => setBorrador((b) => ({ ...b, [campo]: valor }));
  const setServicio = (i, campo, valor) =>
    setBorrador((b) => ({
      ...b,
      servicios: b.servicios.map((s, j) => (j === i ? { ...s, [campo]: valor } : s)),
    }));
  const renumerar = (servicios) => servicios.map((s, i) => ({ ...s, numero: i + 1 }));
  const agregarServicio = () =>
    setBorrador((b) => {
      const ultimo = b.servicios[b.servicios.length - 1];
      return {
        ...b,
        servicios: renumerar([
          ...b.servicios,
          {
            nombre: "",
            dias: ultimo ? Number(ultimo.dias) + 90 : 30,
            kmRef: ultimo ? Number(ultimo.kmRef) + 1500 : 500,
            tolerancia: 15,
            precio: 0,
          },
        ]),
      };
    });
  const quitarServicio = (i) =>
    setBorrador((b) => ({ ...b, servicios: renumerar(b.servicios.filter((_, j) => j !== i)) }));

  // Garantía limitada a piezas: cada pieza con sus meses para nueva y usada.
  const setPieza = (i, campo, valor) =>
    setBorrador((b) => ({
      ...b,
      piezas: (b.piezas || []).map((x, j) => (j === i ? { ...x, [campo]: valor } : x)),
    }));
  const agregarPieza = () =>
    setBorrador((b) => ({
      ...b,
      piezas: [
        ...(b.piezas || []),
        {
          nombre: (b.piezas || []).length ? "" : "Motor",
          mesesNueva: b.mesesNueva || 12,
          mesesUsada: b.mesesUsada || 0,
        },
      ],
    }));
  const quitarPieza = (i) =>
    setBorrador((b) => ({ ...b, piezas: (b.piezas || []).filter((_, j) => j !== i) }));
  const derivados = borrador ? mesesDePiezas(borrador.piezas) : null;
  let ayudaUsadas = derivados ? "De las piezas" : " ";
  if (borrador && Number(derivados ? derivados.mesesUsada : borrador.mesesUsada) === 0) {
    ayudaUsadas = "Usadas sin garantía";
  }

  const guardar = async () => {
    const limpio = {
      ...borrador,
      marca: String(borrador.marca || "").trim(),
      mesesNueva: Number(borrador.mesesNueva),
      mesesUsada: Number(borrador.mesesUsada || 0),
      piezas: (borrador.piezas || []).map((x) => ({
        nombre: String(x.nombre || "").trim(),
        mesesNueva: Number(x.mesesNueva || 0),
        mesesUsada: Number(x.mesesUsada || 0),
      })),
      servicios: borrador.servicios.map((s) => ({
        ...s,
        nombre: String(s.nombre || "").trim(),
        dias: Number(s.dias),
        kmRef: Number(s.kmRef || 0),
        tolerancia: Number(s.tolerancia || 0),
        precio: Number(s.precio || 0),
      })),
    };
    const error = validarPolitica(limpio);
    if (error) return toast.aviso(error);
    const repetida = politicas.find(
      (p) =>
        !p.porDefecto &&
        p.id !== limpio.id &&
        p.marca.trim().toUpperCase() === limpio.marca.toUpperCase()
    );
    if (!limpio.porDefecto && repetida) return toast.aviso(`${repetida.marca} ya tiene política`);
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/garantias_politica_guardar", { politica: limpio });
      if (!ok) {
        toast.error(data?.Error || "No se pudo guardar la política");
        return null;
      }
      toast.exito("Política guardada. Aplica a las ventas nuevas.");
      await cargar(data.id);
    } finally {
      setGuardando(false);
    }
    return null;
  };

  const eliminar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/garantias_politica_eliminar", { id: borrador.id });
      if (!ok) {
        toast.error(data?.Error || "No se pudo eliminar la política");
        return;
      }
      setConfirmarEliminar(false);
      toast.exito("Política eliminada");
      await cargar(politicas.find((p) => p.porDefecto)?.id);
    } finally {
      setGuardando(false);
    }
  };

  const marcasUsadas = politicas.filter((p) => !p.porDefecto).map((p) => p.marca.toUpperCase());
  const marcasSinPolitica = marcasCatalogo.filter(
    (m) => !marcasUsadas.includes(String(m).toUpperCase())
  );

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={3}>
        <Box sx={{ ...sectionBox, p: 1.5 }}>
          <Typography sx={{ ...etiquetaSeccion, px: 1, pb: 1 }}>Marcas</Typography>
          {politicas.map((p) => (
            <Box
              key={p.id}
              onClick={() => setSeleccion(p.id)}
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1.6,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.9rem",
                color: seleccion === p.id ? FP.color.accentBlue : FP.color.textPrimary,
                bgcolor: seleccion === p.id ? FP.color.accentBlueSoft : "transparent",
                "&:hover": {
                  bgcolor: seleccion === p.id ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {nombrePolitica(p)}
              <Typography
                component="span"
                sx={{ fontSize: "0.75rem", color: FP.color.textMuted, fontWeight: 600 }}
              >
                {(p.piezas || []).length ? `${p.piezas.length} piezas · ` : ""}
                {p.servicios.length} serv.
              </Typography>
            </Box>
          ))}
          {marcasSinPolitica.length ? (
            <Typography sx={{ fontSize: "0.75rem", color: FP.color.textMuted, px: 1.25, pt: 1.5 }}>
              Sin política propia (usan la por defecto): {marcasSinPolitica.join(", ")}
            </Typography>
          ) : null}
          {permisos.politicas ? (
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => {
                setSeleccion("nueva");
                setBorrador(nuevaPolitica());
              }}
              sx={{ ...btnOutline(), mt: 1.5 }}
            >
              Nueva marca
            </Button>
          ) : null}
        </Box>
      </Grid>

      <Grid item xs={12} md={9}>
        {borrador ? (
          <Box sx={sectionBox}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
                {borrador.id ? nombrePolitica(borrador) : "Nueva política"}
              </Typography>
              {borrador.porDefecto ? (
                <StatusPill label="Marcas sin política" variant="info" />
              ) : null}
            </Stack>

            <Grid container spacing={1.5}>
              {!borrador.porDefecto ? (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={marcasSinPolitica}
                    value={borrador.marca}
                    onInputChange={(_, v) => set("marca", v)}
                    disabled={!!borrador.id}
                    renderInput={(params) => <TextField {...params} label="Marca" sx={fpInputSx} />}
                  />
                </Grid>
              ) : null}
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Meses (nuevas)"
                  value={derivados ? derivados.mesesNueva : borrador.mesesNueva}
                  onChange={(e) => set("mesesNueva", soloEntero(e.target.value))}
                  inputProps={{ inputMode: "numeric" }}
                  disabled={!!derivados}
                  helperText={derivados ? "De las piezas" : " "}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Meses (usadas)"
                  value={derivados ? derivados.mesesUsada : borrador.mesesUsada}
                  onChange={(e) => set("mesesUsada", soloEntero(e.target.value))}
                  inputProps={{ inputMode: "numeric" }}
                  disabled={!!derivados}
                  helperText={ayudaUsadas}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Cubre"
                  value={borrador.cubre}
                  onChange={(e) => set("cubre", e.target.value)}
                  sx={fpMultilineInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="No cubre"
                  value={borrador.noCubre}
                  onChange={(e) => set("noCubre", e.target.value)}
                  sx={fpMultilineInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Texto del certificado"
                  placeholder="Condiciones, compromisos del cliente, cómo hacer un reclamo, teléfono del taller…"
                  value={borrador.descripcion || ""}
                  onChange={(e) => set("descripcion", e.target.value)}
                  inputProps={{ maxLength: 4000 }}
                  helperText="Sale en el certificado debajo de los datos de la venta."
                  sx={fpMultilineInputSx}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 3, mb: 0.5 }}
            >
              <Typography sx={etiquetaSeccion}>Piezas cubiertas</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={agregarPieza} sx={btnOutline()}>
                Pieza
              </Button>
            </Stack>
            <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mb: 1 }}>
              {(borrador.piezas || []).length
                ? "Garantía limitada: solo cubre estas piezas, cada una por sus meses (0 = no cubre en esa condición). La garantía vence con la que más dura."
                : "Sin piezas, la garantía es general: cubre lo descrito arriba por los meses de la política. Agregue piezas para limitarla."}
            </Typography>
            {(borrador.piezas || []).map((x, i) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 120px 120px 40px",
                  gap: 1,
                  alignItems: "center",
                  py: 0.6,
                }}
              >
                <TextField
                  size="small"
                  label={i === 0 ? "Pieza" : undefined}
                  value={x.nombre}
                  onChange={(e) => setPieza(i, "nombre", e.target.value)}
                />
                <TextField
                  size="small"
                  label={i === 0 ? "Meses nuevas" : undefined}
                  value={x.mesesNueva}
                  onChange={(e) => setPieza(i, "mesesNueva", soloEntero(e.target.value))}
                  inputProps={{ inputMode: "numeric" }}
                />
                <TextField
                  size="small"
                  label={i === 0 ? "Meses usadas" : undefined}
                  value={x.mesesUsada}
                  onChange={(e) => setPieza(i, "mesesUsada", soloEntero(e.target.value))}
                  inputProps={{ inputMode: "numeric" }}
                />
                <Tooltip title="Quitar pieza">
                  <IconButton size="small" onClick={() => quitarPieza(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 3, mb: 1 }}
            >
              <Typography sx={etiquetaSeccion}>Plan de mantenimientos</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={agregarServicio}
                sx={btnOutline()}
              >
                Servicio
              </Button>
            </Stack>

            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 640 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "28px 1fr 80px 90px 90px 100px 40px",
                    gap: 1,
                    px: 0.5,
                    pb: 0.5,
                    ...etiquetaSeccion,
                    fontSize: "0.64rem",
                  }}
                >
                  <span>#</span>
                  <span>Servicio</span>
                  <span>Días</span>
                  <span>Km ref.</span>
                  <span>Tolerancia ±d</span>
                  <span>Precio RD$</span>
                  <span />
                </Box>
                {borrador.servicios.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "28px 1fr 80px 90px 90px 100px 40px",
                      gap: 1,
                      alignItems: "center",
                      py: 0.6,
                    }}
                  >
                    <Typography sx={{ color: FP.color.textMuted, fontWeight: 700, pl: 0.5 }}>
                      {s.numero}
                    </Typography>
                    <TextField
                      size="small"
                      value={s.nombre}
                      onChange={(e) => setServicio(i, "nombre", e.target.value)}
                    />
                    <TextField
                      size="small"
                      value={s.dias}
                      onChange={(e) => setServicio(i, "dias", soloEntero(e.target.value))}
                      inputProps={{ inputMode: "numeric" }}
                    />
                    <TextField
                      size="small"
                      value={s.kmRef}
                      onChange={(e) => setServicio(i, "kmRef", soloEntero(e.target.value))}
                      inputProps={{ inputMode: "numeric" }}
                    />
                    <TextField
                      size="small"
                      value={s.tolerancia}
                      onChange={(e) => setServicio(i, "tolerancia", soloEntero(e.target.value))}
                      inputProps={{ inputMode: "numeric" }}
                    />
                    <TextField
                      size="small"
                      value={s.precio}
                      onChange={(e) =>
                        setServicio(i, "precio", String(e.target.value).replace(/[^\d.]/g, ""))
                      }
                      inputProps={{ inputMode: "decimal" }}
                    />
                    <Tooltip title="Quitar servicio">
                      <IconButton size="small" onClick={() => quitarServicio(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 2, color: FP.color.textSecondary }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: "0.8rem" }}>
                Los días cuentan desde la venta y precio 0 es gratis. Los cambios aplican a las
                ventas nuevas; las garantías ya emitidas conservan su plan.
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2.5 }}>
              {borrador.id && !borrador.porDefecto && permisos.eliminarPolitica ? (
                <Button
                  onClick={() => setConfirmarEliminar(true)}
                  sx={{ ...btnBase, color: FP.color.danger }}
                >
                  Eliminar política
                </Button>
              ) : null}
              {permisos.politicas ? (
                <Button onClick={guardar} disabled={guardando} sx={btnSolid()}>
                  Guardar política
                </Button>
              ) : null}
            </Stack>
          </Box>
        ) : null}
        {permisos.cargaInicial ? <CargaInicial toast={toast} /> : null}
      </Grid>

      <ConfirmarDialog
        abierto={confirmarEliminar}
        titulo="Eliminar política"
        mensaje={`Las ventas nuevas de ${
          borrador?.marca || ""
        } usarán la política por defecto. Las garantías ya emitidas no cambian.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={guardando}
        onCerrar={() => setConfirmarEliminar(false)}
        onConfirmar={eliminar}
      />
    </Grid>
  );
}
